import { useEffect } from "react"
import {
  Toast,
  toast as rht,
  ToastOptions as RHTToastOptions,
} from "react-hot-toast"
import { getUserMessage, isApiError } from "shared-ts/api/apiError"

import { classNames } from "./classNames"
// The one sanctioned use of the deprecated banner: toasts render it until the
// new Toast ships, at which point both this import and the component go.
// eslint-disable-next-line no-restricted-imports
import {
  LegacyBanner,
  LegacyBannerProps,
} from "../components/base/informational/Banner/Banner"

type Options = RHTToastOptions &
  Pick<
    LegacyBannerProps,
    "actionButtonMessage" | "actionButtonMode" | "onClickActionButton"
  >

type ToastProps = {
  message: string
  options?: Options
  error?: unknown // ApiError's with user messages override the default message
}

/**
 * How long each severity stays on screen. A confirmation is disposable, while an
 * error or warning is asking the reader to do something, so those get longer.
 *
 * This is also the window in which a repeat is folded into the toast already
 * showing rather than stacked beneath it, because a count is only honest while
 * the thing being counted is still visible. Passing these per toast rather than
 * configuring each `<Toaster>` keeps the two meanings the same number by
 * construction — five Toasters across web and web-cxa would otherwise each need
 * to agree with this file.
 */
const durationsMs = {
  success: 4000,
  information: 4000,
  warning: 5000,
  error: 5000,
  copy: 2000,
} as const

const landed = new Map<string, { count: number; expiresAt: number }>()

/**
 * Test-only. Jest suites wipe react-hot-toast's store between tests with
 * `toast.remove()`, which unmounts CoalescedToast without ever flipping
 * `visible` to false, so `forget` never runs and a count from one test would
 * fold into the next test's toast ("Changes saved (2)"). Clearing the map
 * alongside the store keeps the two in step.
 */
export function resetToastCoalescing() {
  landed.clear()
}

/**
 * What the reader will actually see. The banner shows an ApiError's user message
 * in place of the fallback it was handed, so grouping has to key on this rather
 * than on `message`: unrelated failures routinely share one generic fallback
 * ("Something went wrong. Please try again."), and keying on that would merge
 * two different errors and destroy the first one's message.
 */
function userFacing(message: string, error?: unknown) {
  return (isApiError(error) && getUserMessage(error)) || message
}

/**
 * Collapses repeats of one message into a single toast carrying a count. A
 * retrying query fires the same error on every attempt, and every settings
 * screen reports "Changes saved", so both bury the page under a wall of
 * identical banners. Only exact repeats merge: a differently-worded toast is a
 * different fact and its own wording is the useful part, so it keeps its own
 * banner rather than being counted into a generic summary.
 *
 * The returned id is reused as react-hot-toast's own id — passing an id that is
 * already showing updates that toast in place instead of stacking another.
 */
function coalesce(
  mode: keyof typeof durationsMs,
  message: string,
  options?: Options,
  error?: unknown
) {
  const text = userFacing(message, error)
  const duration = options?.duration ?? durationsMs[mode]

  /*
   * A toast offering an action cannot be merged. The callback closes over the
   * record it was raised for, so folding two together would leave one banner
   * whose link reaches only the later record. Without an id react-hot-toast
   * stacks them, which is the right outcome here.
   */
  if (options?.onClickActionButton) {
    return { id: undefined, duration, forget: () => undefined, text, count: 1 }
  }

  const key = `${mode}:${text}`
  const now = Date.now()
  const prior = landed.get(key)
  const count = prior && prior.expiresAt > now ? prior.count + 1 : 1
  landed.set(key, { count, expiresAt: now + duration })
  return { id: key, duration, forget: () => landed.delete(key), text, count }
}

interface CoalescedToastProps
  extends Omit<LegacyBannerProps, "onClose" | "message"> {
  toast: Toast
  message: string
  forget: () => void
}

function CoalescedToast({
  toast: t,
  forget,
  className,
  ...bannerProps
}: CoalescedToastProps) {
  /*
   * Every dismissal path — the X, an explicit rht.dismiss, or the duration
   * elapsing — flips visible to false before unmount, and that is the only
   * signal all three share. Without it a programmatic dismiss would leave the
   * count behind and the next occurrence would resume counting from it.
   */
  useEffect(() => {
    if (!t.visible) forget()
  }, [t.visible, forget])

  return (
    <LegacyBanner
      {...bannerProps}
      className={classNames(
        t.visible ? "animate-enter" : "animate-leave",
        className
      )}
      onClose={() => {
        forget()
        rht.dismiss(t.id)
      }}
    />
  )
}

function success({ message, options }: Omit<ToastProps, "error">) {
  const {
    actionButtonMessage,
    actionButtonMode,
    onClickActionButton,
    duration: _duration,
    ...rhtOptions
  } = options ?? {}
  const { id, duration, forget, text, count } = coalesce(
    "success",
    message,
    options
  )

  return rht.custom(
    (t) => (
      <CoalescedToast
        toast={t}
        forget={forget}
        repeatCount={count}
        data-testid="success-toast"
        mode="success"
        className="w-full max-w-3xl"
        message={text}
        actionButtonMessage={actionButtonMessage}
        actionButtonMode={actionButtonMode}
        onClickActionButton={onClickActionButton}
      />
    ),
    { id, ...rhtOptions, duration }
  )
}

function error({ message, options, error }: ToastProps) {
  const {
    actionButtonMessage,
    actionButtonMode,
    onClickActionButton,
    duration: _duration,
    ...rhtOptions
  } = options ?? {}
  const { id, duration, forget, text, count } = coalesce(
    "error",
    message,
    options,
    error
  )

  return rht.custom(
    (t) => (
      <CoalescedToast
        toast={t}
        forget={forget}
        repeatCount={count}
        data-testid="error-toast"
        mode="error"
        className="w-full max-w-3xl"
        message={text}
        error={error}
        loggerScope="toast"
        actionButtonMessage={actionButtonMessage}
        actionButtonMode={actionButtonMode}
        onClickActionButton={onClickActionButton}
      />
    ),
    { id, ...rhtOptions, duration }
  )
}

function warning({ message, options, error }: ToastProps) {
  const {
    actionButtonMessage,
    actionButtonMode,
    onClickActionButton,
    duration: _duration,
    ...rhtOptions
  } = options ?? {}
  const { id, duration, forget, text, count } = coalesce(
    "warning",
    message,
    options,
    error
  )

  return rht.custom(
    (t) => (
      <CoalescedToast
        toast={t}
        forget={forget}
        repeatCount={count}
        mode="warning"
        className="w-full max-w-3xl"
        message={text}
        error={error}
        loggerScope="toast"
        actionButtonMessage={actionButtonMessage}
        actionButtonMode={actionButtonMode}
        onClickActionButton={onClickActionButton}
      />
    ),
    { id, ...rhtOptions, duration }
  )
}

function info({ message, options, error }: ToastProps) {
  const {
    actionButtonMessage,
    actionButtonMode,
    onClickActionButton,
    duration: _duration,
    ...rhtOptions
  } = options ?? {}
  const { id, duration, forget, text, count } = coalesce(
    "information",
    message,
    options,
    error
  )

  return rht.custom(
    (t) => (
      <CoalescedToast
        toast={t}
        forget={forget}
        repeatCount={count}
        mode="information"
        className="w-full max-w-3xl"
        message={text}
        error={error}
        loggerScope="toast"
        actionButtonMessage={actionButtonMessage}
        actionButtonMode={actionButtonMode}
        onClickActionButton={onClickActionButton}
      />
    ),
    { id, ...rhtOptions, duration }
  )
}

function copy({ message, options, error }: ToastProps) {
  const {
    actionButtonMessage,
    actionButtonMode,
    onClickActionButton,
    duration: _duration,
    ...rhtOptions
  } = options ?? {}
  const { id, duration, forget, text, count } = coalesce(
    "copy",
    message,
    options,
    error
  )

  return rht.custom(
    (t) => (
      <CoalescedToast
        toast={t}
        forget={forget}
        repeatCount={count}
        data-testid="copy-toast"
        mode="copy"
        className={classNames(
          "w-1/4 max-w-xs",
          "surface-primary",
          "text-primary",
          "shadow-[0_2px_14px_0px_rgba(0,0,0,0.15)]"
        )}
        message={text}
        error={error}
        loggerScope="toast"
        actionButtonMessage={actionButtonMessage}
        actionButtonMode={actionButtonMode}
        onClickActionButton={onClickActionButton}
      />
    ),
    { id, ...rhtOptions, duration }
  )
}

export const toast = {
  success,
  error,
  warning,
  info,
  copy,
}
